import { useCallback, useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import Sheet, { SheetRef } from 'react-modal-sheet'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  answerCheckSelector,
  answerSelector,
  counterSelector,
  currentNumberSelector,
  finishQuiz,
  lengthSelector,
  setCounter,
  setNextQuestion,
} from '@/services/slices/questionSlice'
import { AppDispatch } from '@/store'
import Button from '@/ui/Button/Button'
import QuestionCounter from '@/ui/QuestionCounter/QuestionCounter'
import { createResult } from '@/utils/api'
import classNames from 'classnames'
import rehypeRaw from 'rehype-raw'

import styles from './AnswerCardMobile.module.scss'

import AnswerCheck from '../../AnswerCheck/AnswerCheck'

const snapPoints = [-82, 0.5, 235]
const initialSnap = snapPoints.length - 1

const AnswerCardMobile = () => {
  const ref = useRef<SheetRef>()
  const [render, setRender] = useState(false)
  const [isOpen, setOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setCurrIndex] = useState(initialSnap)

  const snapTo = useCallback((i: number) => ref.current?.snapTo(i), [])

  const onSnap = useCallback(
    (index: number) => {
      if (render) {
        setOpen(!index)

        setCurrIndex(prevIndex => {
          if (prevIndex === 0 && prevIndex !== index) snapTo(initialSnap)
          if (prevIndex === 2 && prevIndex !== index) snapTo(0)

          return index
        })
      }
    },
    [snapTo, render],
  )

  const close = useCallback(() => {
    snapTo(snapPoints.length - 1)
    setOpen(false)
  }, [snapTo])

  useEffect(() => {
    setRender(true)
  }, [])

  const length = useSelector(lengthSelector)
  const number = useSelector(currentNumberSelector)
  const { right, text, name } = useSelector(answerSelector)
  const questionNumber = useSelector(currentNumberSelector)
  const lastQuestionNumber = useSelector(lengthSelector)
  const checkedAnswer = useSelector(answerCheckSelector)
  const corrects = useSelector(counterSelector)

  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const onNext = useCallback(() => {
    if (checkedAnswer) {
      dispatch(setCounter())
    }
    dispatch(setNextQuestion(questionNumber))
  }, [checkedAnswer, dispatch, questionNumber])

  const onResults = useCallback(async () => {
    dispatch(createResult({ corrects, questions: lastQuestionNumber })).then(
      state => {
        navigate(`/results/${state.payload.id}`)
      },
    )
  }, [corrects, dispatch, lastQuestionNumber, navigate])

  const onLanding = () => {
    dispatch(finishQuiz())
  }

  return (
    <Sheet
      isOpen
      ref={ref}
      onSnap={onSnap}
      onClose={close}
      disableScrollLocking
      snapPoints={snapPoints}
      initialSnap={initialSnap}
    >
      <Sheet.Container className={styles.card}>
        <Sheet.Header className={styles.card_swipe}>
          <div className={styles.card_swipe_divider} />
        </Sheet.Header>
        <Sheet.Content>
          <Sheet.Scroller
            draggableAt="both"
            className={classNames(
              styles.container,
              isOpen && styles.container_open,
            )}
          >
            <div className={styles.content}>
              <h1
                className={classNames(
                  'text',
                  't-caption',
                  'text_color_tertiary',
                  styles.heading,
                )}
              >
                {`Вопрос ${number} из ${length}`}
              </h1>
              <div className={styles.answer_wrapper}>
                <p
                  className={classNames(
                    'text',
                    't-header-h5',
                    'text_color_primary',
                    styles.text,
                  )}
                >{`Ваш ответ: ${name}`}</p>
                <AnswerCheck right={right} />
              </div>
              <div>
                <Markdown
                  components={{
                    a: ({ href, children }) => (
                      <a href={href} target="_blank">
                        {children}
                      </a>
                    ),
                  }}
                  rehypePlugins={[rehypeRaw]}
                  className={classNames(
                    'text',
                    't-body',
                    'text_color_primary',
                    styles.texts,
                  )}
                >
                  {text}
                </Markdown>
              </div>
            </div>

            <QuestionCounter />

            <div className={styles.button_container}>
              <Button
                className={classNames(
                  styles.button,
                  isOpen && styles.button_open,
                )}
                type="accent"
                size={'fullsize'}
                onClick={
                  questionNumber === lastQuestionNumber ? onResults : onNext
                }
              >
                {questionNumber === lastQuestionNumber
                  ? 'Посмотреть результат'
                  : 'Следующий вопрос'}
              </Button>
              <Button
                className={classNames(
                  styles.button,
                  styles.button_border,
                  isOpen && styles.button_open,
                )}
                type="primary"
                size="fullsize"
                linkTo="/#records"
                onClick={onLanding}
              >
                <span>Завершить игру</span>
              </Button>
            </div>
          </Sheet.Scroller>
        </Sheet.Content>
      </Sheet.Container>
      <div className={classNames(styles.blur, isOpen && styles.blur_open)}>
        <Button
          className={styles.blur_button}
          type="accent"
          size="fullsize"
          onClick={questionNumber === lastQuestionNumber ? onResults : onNext}
        >
          {questionNumber === lastQuestionNumber
            ? 'Посмотреть результат'
            : 'Следующий вопрос'}
        </Button>
      </div>
    </Sheet>
  )
}

export default AnswerCardMobile
